// Combat VFX stack.
//
// The first graybox kept most feedback on a single normal-blend Graphics object.
// That made white hits, weapon light, blood and telegraphs compete for the same
// visual weight. This stack separates grounding, emissive light, contact sparks,
// weather and screen response so each can be tuned without changing simulation.

import { SPECIES, spriteBottomOffsetY } from './enemyart.js';
import { ACT, EQUIP_FX, GRASSHOPPER, cardIdOf } from './config.js';
import { TrajectoryTrail, drawTrailBatch } from './trajectory.js';
import {
  chooseScreenResponse,
  bossWarningProgress,
  diamondBeat,
  diamondLocalAlpha,
  diamondSignature,
  enemyWarningProgress,
  isDiamondSetPresentation,
  resolveEnemyBehavior,
} from './presentation.js';

const W = 1920;
const H = 1080;
const TAU = Math.PI * 2;
const SOFT_LIGHT_KEY = 'gb-soft-key-light-v3';
const DOG_CELL = 48;
const DOG_ALPHA_BOTTOM = 44;
const BEHAVIOR_MARK_VIEW_MARGIN = 160;
// 일반 물량층의 동시 접촉은 기존 회전 타격 눈금 상한(4)까지만 개별로 읽힌다.
// 세 번째 일반 처치부터는 scene의 combo 신호가 묶음 수를 맡으므로 개별 kill burst는
// 가장 가까운 세 개면 충분하다. 정예와 ◆ 레이어는 이 두 상한을 소비하지 않는다.
const SWARM_HIT_FX_CAP = 4;
const NORMAL_KILL_FX_CAP = 3;
// 모난 돌은 파편 수가 많아 공용 VFX를 무작정 키우면 섬광 덩어리가 된다. 실제 탄체의
// 방향과 세대만 조금 더 오래·굵게 읽히도록 제한한다. 이 값은 판정에 닿지 않는다.
export const MONAN_SHARD_VISUAL = Object.freeze({
  headScale: 1.12,
  trailAgeMul: 1.18,
  trailLengthMul: 1.16,
  generationFade: 0.14,
  eventLife: 0.28,
  maxDirectionalMarks: 14,
});

// 최신 파편 사건과 같은 세대의 실제 탄체만 고른다. `_shardCount`는 그 사건에서
// 태어난 수이므로 원점에 가까운 순서로 그 수만큼만 잡으면, 예전 파편이나 다른 돌을
// 가짜 방사선으로 섞지 않는다. 반환 방향은 오직 실제 vx/vy에서 나온다.
export function liveMonanShardVectors(world) {
  const origin = world?._shardXY;
  const age = (world?.t ?? 0) - (world?._shardAt ?? -9);
  if (!origin || age < 0 || age >= MONAN_SHARD_VISUAL.eventLife) return [];
  const generation = Math.max(1, world._shardGeneration | 0);
  const event = world._shardSeq | 0;
  const eventCount = Math.max(0, world._shardCount | 0);
  const limit = Math.min(MONAN_SHARD_VISUAL.maxDirectionalMarks, eventCount);
  if (limit <= 0) return [];
  const candidates = [];
  for (const shot of world.shots ?? []) {
    if (!shot.shard || shot.stoneId !== 'monan' || (shot.shardEvent | 0) !== event
        || Math.max(1, shot.shardGen | 0) !== generation) continue;
    const speed = Math.hypot(shot.vx, shot.vy);
    if (!(speed > 0)) continue;
    const dx = shot.x - origin.x, dy = shot.y - origin.y;
    candidates.push({
      shot, ux: shot.vx / speed, uy: shot.vy / speed,
      dx, dy, distance: Math.hypot(dx, dy), d2: dx * dx + dy * dy,
      generation,
    });
  }
  candidates.sort((a, b) => a.d2 - b.d2);
  return candidates.slice(0, limit);
}
const GROUND_SHADOW = Object.freeze({
  'field-rat':       { w: 20, h: 5,  a: 0.30 },
  'jackal':          { w: 21, h: 6,  a: 0.32 },
  'grasshopper':     { w: 18, h: 5,  a: 0.28 },
  'wild-dog':        { w: 21, h: 6,  a: 0.32 },
  'wolf':            { w: 35, h: 9,  a: 0.40 },
  'wild-dog-leader': { w: 35, h: 10, a: 0.43 },
  'hyena':           { w: 35, h: 10, a: 0.43 },
});
const AIR_SHADOW = Object.freeze({
  'bat':  { y: 14, w: 13, h: 5, a: 0.16 },
  'crow': { y: 21, w: 16, h: 6, a: 0.18 },
});
const BOSS_SHADOW = Object.freeze({
  'bear': { w: 86,  h: 22, a: 0.50 },
  'lion': { w: 154, h: 38, a: 0.52 },
});

// Shared combat language, distinct night identities.
// `grade` compresses the field, `lift` colors the upper atmosphere, and
// `atmosphere` selects a lightweight deterministic weather treatment.
const NIGHT_LOOK = {
  N1:  { grade: 0x09111c, gradeA: 0.08, lift: 0x304b65, liftA: 0.025,
         edge: 0x07101c, atmosphere: 'embers', key: 0xffad61 },
  N3:  { grade: 0x150b12, gradeA: 0.11, lift: 0x5a2630, liftA: 0.030,
         edge: 0x210811, atmosphere: 'dust', key: 0xffa65a },
  N8:  { grade: 0x070819, gradeA: 0.17, lift: 0x28355e, liftA: 0.045,
         edge: 0x050613, atmosphere: 'shadows', key: 0xd69cff },
  N10: { grade: 0x160d07, gradeA: 0.13, lift: 0x6a431e, liftA: 0.035,
         edge: 0x1d0b04, atmosphere: 'canyon', key: 0xffb35e },
  N12: { grade: 0x180810, gradeA: 0.15, lift: 0x61243e, liftA: 0.034,
         edge: 0x24050e, atmosphere: 'ambush', key: 0xff9b64 },
  N15: { grade: 0x061426, gradeA: 0.30, lift: 0x183f60, liftA: 0.070,
         edge: 0x04101f, atmosphere: 'rain', key: 0xffa15a },
  N20: { grade: 0x061311, gradeA: 0.15, lift: 0x185348, liftA: 0.035,
         edge: 0x031612, atmosphere: 'crows', key: 0xffb057 },
  N22: { grade: 0x0a1120, gradeA: 0.12, lift: 0x536b98, liftA: 0.040,
         edge: 0x07101f, atmosphere: 'guides', key: 0xffc77b },
  N23: { grade: 0x09101a, gradeA: 0.15, lift: 0x395772, liftA: 0.035,
         edge: 0x06101c, atmosphere: 'wind', key: 0xffaa55 },
  // ⚠ 열쇠는 **구 카드 번호**다 — 찾을 때 `cardIdOf()` 로 되찾는다(아래 사용처 참조).
  N30: { grade: 0x1a0704, gradeA: 0.20, lift: 0x762918, liftA: 0.050,
         edge: 0x2a0503, atmosphere: 'cinders', key: 0xff8f42 },
};

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const hash = (n) => {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};
const wrap = (v, span) => ((v % span) + span) % span;

function resetNearestSlots(slots) {
  for (const slot of slots) { slot.item = null; slot.d2 = Infinity; }
}

function considerNearest(slots, item, d2) {
  let worst = 0;
  for (let i = 1; i < slots.length; i++) {
    if (slots[i].d2 > slots[worst].d2) worst = i;
  }
  if (d2 >= slots[worst].d2) return;
  slots[worst].item = item;
  slots[worst].d2 = d2;
}

const selectedNearest = (slots, item) => slots.some((slot) => slot.item === item);

function ensureSoftLight(scene, Phaser) {
  if (!scene.textures.exists(SOFT_LIGHT_KEY)) {
    const texture = scene.textures.createCanvas(SOFT_LIGHT_KEY, 512, 512);
    const ctx = texture.getContext();
    const gradient = ctx.createRadialGradient(256, 256, 2, 256, 256, 256);
    gradient.addColorStop(0, 'rgba(255,255,255,0.98)');
    gradient.addColorStop(0.08, 'rgba(255,255,255,0.78)');
    gradient.addColorStop(0.28, 'rgba(255,255,255,0.40)');
    gradient.addColorStop(0.62, 'rgba(255,255,255,0.12)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 512, 512);
    texture.refresh();
    texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
  }
}

function pulse(age, life) {
  if (age < 0 || age >= life) return 0;
  const t = age / life;
  return (1 - t) * (1 - t);
}

function drawBrokenTrack(g, x0, y0, x1, y1, progress, color, alpha, width = 2, maxMarks = 7) {
  const dx = x1 - x0, dy = y1 - y0, d = Math.hypot(dx, dy);
  if (!(d > 1e-5)) return 0;
  const ux = dx / d, uy = dy / d;
  const marks = Math.max(2, Math.min(maxMarks, Math.ceil(d / 72)));
  for (let i = 0; i < marks; i++) {
    const q = (i + 0.55) / marks;
    const reveal = clamp01(progress * (marks + 1) - i);
    if (reveal <= 0) continue;
    const along = d * q;
    const len = Math.min(30, d / marks * 0.56) * (0.72 + 0.28 * reveal);
    const cx = x0 + ux * along, cy = y0 + uy * along;
    g.lineStyle(width + 1.2, 0x080504, alpha * reveal * 0.22);
    g.lineBetween(cx - ux * len * 0.5, cy - uy * len * 0.5,
                  cx + ux * len * 0.5, cy + uy * len * 0.5);
    g.lineStyle(width, color, alpha * reveal);
    g.lineBetween(cx - ux * len * 0.5, cy - uy * len * 0.5,
                  cx + ux * len * 0.5, cy + uy * len * 0.5);
  }
  return marks;
}

export class CombatVfx {
  constructor(scene, Phaser, nightKey) {
    this.scene = scene;
    this.Phaser = Phaser;
    this.nightKey = nightKey;
    // ⚠ **열쇠는 구 카드 번호다** 【40일 개편 2026-08-03】. `NIGHT_LOOK` 은 앵커 열 밤에
    //   맞춰 만든 표이고 그 이름은 **그 카드**를 뜻한다. 40일에서 같은 이름의 날짜가
    //   딴 밤이 됐으므로, 그 밤이 **어느 카드에서 왔는지**를 먼저 되찾는다.
    //   안 그러면 곰의 밤이 평범한 색으로 서고 엉뚱한 밤이 핏빛이 된다.
    this.look = NIGHT_LOOK[cardIdOf(nightKey)] ?? NIGHT_LOOK[nightKey] ?? NIGHT_LOOK.N1;
    this.storm = this.look.atmosphere === 'rain';

    // Flat grade above the field, below every gameplay object.
    this.grade = scene.add.graphics().setDepth(2).setScrollFactor(0);
    // Contact shadows and rain strikes live under actors.
    this.ground = scene.add.graphics().setDepth(17);
    // Broad light is additive and stays behind actor sprites.
    this.glow = scene.add.graphics().setDepth(19)
      .setBlendMode(Phaser.BlendModes.ADD);
    ensureSoftLight(scene, Phaser);
    this.keyLight = scene.add.image(0, 0, SOFT_LIGHT_KEY).setDepth(18)
      .setBlendMode(Phaser.BlendModes.ADD).setTint(this.look.key);
    // Sparks, shock rings and contact flashes sit over actors.
    this.impact = scene.add.graphics().setDepth(46)
      .setBlendMode(Phaser.BlendModes.ADD);
    // Rain is screen-space so camera movement never changes its angle or density.
    this.weather = scene.add.graphics().setDepth(72).setScrollFactor(0);
    // Screen-space local response: warning marks, lightning lines and combo edge strokes.
    this.screenGlow = scene.add.graphics().setDepth(86).setScrollFactor(0)
      .setBlendMode(Phaser.BlendModes.ADD);
    // Normal-blend edge grade preserves black levels under the local additive marks.
    this.screenGrade = scene.add.graphics().setDepth(84).setScrollFactor(0);

    this.rain = Array.from({ length: 148 }, (_, i) => ({
      x: hash(i + 11) * (W + 360) - 180,
      y: hash(i + 131) * (H + 260) - 130,
      speed: 760 + hash(i + 271) * 1180,
      len: 13 + hash(i + 431) * 46,
      width: hash(i + 593) > 0.83 ? 2.6 : 1.2,
      alpha: 0.10 + hash(i + 719) * 0.34,
      drift: 0.19 + hash(i + 887) * 0.11,
    }));
    this.ripples = Array.from({ length: 34 }, (_, i) => ({
      x: 45 + hash(i + 1009) * (W - 90),
      y: 80 + hash(i + 1171) * (H - 120),
      phase: hash(i + 1291),
      rate: 0.55 + hash(i + 1453) * 0.9,
      size: 8 + hash(i + 1613) * 18,
    }));
    this.motes = Array.from({ length: 88 }, (_, i) => ({
      x: hash(i + 1823) * W,
      y: hash(i + 1999) * H,
      phase: hash(i + 2137),
      speed: 16 + hash(i + 2311) * 96,
      drift: -0.8 + hash(i + 2477) * 1.6,
      size: 0.7 + hash(i + 2633) * 2.4,
      bright: hash(i + 2791),
    }));
    this.gusts = Array.from({ length: 28 }, (_, i) => ({
      x: hash(i + 2953) * (W + 600) - 300,
      y: hash(i + 3109) * H,
      speed: 170 + hash(i + 3271) * 520,
      len: 34 + hash(i + 3449) * 170,
      alpha: 0.03 + hash(i + 3613) * 0.12,
    }));

    // One render history per shot identity. Removed shots remain in this small
    // state list just long enough to append their final collision coordinate,
    // then naturally contract instead of snapping out of existence.
    this.shotTrailMap = new WeakMap();
    this.shotTrailStates = [];
    this.mainTrails = [];
    this.shardTrails = [];
    this.trailFrame = 0;
    this.mainTrailPasses = [
      { width: 9.0, alpha: 0.10, color: 0xffffff },
      { width: 3.8, alpha: 0.46, color: 0xffffff },
      { width: 1.25, alpha: 0.88, color: 0xfff5dd, start: 0.56 },
    ];
    this.shardTrailPasses = [
      { width: 3.4, alpha: 0.11, color: 0xffffff },
      { width: 1.25, alpha: 0.50, color: 0xffffff },
    ];
    this._behaviorScratch = {};
    this._behaviorFxCounts = { major: 0, motion: 0, recover: 0, boss: 0, total: 0 };
    const nearestSlots = (count) => Array.from({ length: count }, () => ({
      item: null, d2: Infinity,
    }));
    this._contactFxNearest = {
      hits: nearestSlots(SWARM_HIT_FX_CAP),
      kills: nearestSlots(NORMAL_KILL_FX_CAP),
    };
  }

  destroy() {
    for (const g of [this.grade, this.ground, this.glow, this.impact,
                     this.weather, this.screenGlow, this.screenGrade]) {
      g?.destroy();
    }
    this.keyLight?.destroy();
  }

  draw(world, player, artScale, tierColor) {
    world._presentationFrame = this.scene._artClock ?? world.t;
    for (const g of [this.grade, this.ground, this.glow, this.impact,
                     this.weather, this.screenGlow, this.screenGrade]) g.clear();

    this.drawFieldGrade();
    this.drawGrounding(world, player, artScale);
    this.drawActorLight(world, player, artScale, tierColor);
    this.drawContactFx(world, artScale, tierColor);
    this.drawDiamondWeaponSignature(world, player, artScale, tierColor);
    this.drawDiamondSetFireworks(world, player, artScale, tierColor);
    this.drawWeather(world);
    this.drawScreenResponse(world, tierColor);
  }

  drawFieldGrade() {
    const g = this.grade;
    // Every night has its own color separation, but none is a visibility mask.
    // The field remains readable while attacks own the brightest values.
    g.fillStyle(this.look.grade, this.look.gradeA);
    g.fillRect(0, 0, W, H);
    g.fillStyle(this.look.lift, this.look.liftA);
    g.fillRect(0, 0, W, H * 0.50);
  }

  drawGrounding(world, player, artScale) {
    const g = this.ground;
    const enemyScale = this.scene.enemyScale ?? artScale;

    // A readable silhouette begins with a contact shadow. Enemy bodies no
    // longer have to fight every pebble in the field texture.
    g.fillStyle(0x02040a, 0.52);
    g.fillEllipse(player.x, player.y + 7 * artScale, 40 * artScale, 15 * artScale);
    for (const e of world.enemies) {
      const r = Math.max(7, e.r);
      const spec = SPECIES[e._sp];
      if (!spec) {
        // Graybox/fallback actors are circles, so their contact point is the
        // lower edge rather than the old body-centre-biased r * 0.33.
        g.fillStyle(0x010309, e.kind === 'swarm' ? 0.32 : 0.44);
        g.fillEllipse(e.x, e.y + r * 0.82, r * 2.15, r * 0.72);
        continue;
      }

      if (spec.fly) {
        // Airborne actors have no ground contact. Keep their projected shadow
        // directly below the body and softer than a planted creature.
        const profile = AIR_SHADOW[e._sp]
          ?? { y: spec.span * 0.75, w: spec.span * 0.62, h: spec.span * 0.18, a: 0.16 };
        g.fillStyle(0x010309, profile.a);
        g.fillEllipse(
          e.x,
          e.y + profile.y * enemyScale,
          profile.w * enemyScale,
          profile.h * enemyScale,
        );
        continue;
      }

      const plantedProfile = GROUND_SHADOW[e._sp]
        ?? { w: spec.span * 0.86, h: spec.span * 0.22, a: 0.36 };
      let profile = plantedProfile;
      if (e._sp === 'grasshopper' && e.motionState === 'air') {
        const u = Math.max(0, Math.min(1,
          (Number(e.motionT) || 0) / GRASSHOPPER.phaseSec));
        const arc = Math.sin(Math.PI * u);
        const airborneProfile = { w: spec.span * 0.62, h: spec.span * 0.18, a: 0.16 };
        profile = {
          w: plantedProfile.w + (airborneProfile.w - plantedProfile.w) * arc,
          h: plantedProfile.h + (airborneProfile.h - plantedProfile.h) * arc,
          a: plantedProfile.a + (airborneProfile.a - plantedProfile.a) * arc,
        };
      }
      const contactY = e.y + spriteBottomOffsetY(e._sp, enemyScale) - enemyScale * 0.5;
      g.fillStyle(0x010309, profile.a);
      g.fillEllipse(
        e.x,
        contactY,
        profile.w * enemyScale,
        profile.h * enemyScale,
      );
    }
    for (const dog of world.dogs ?? []) {
      const profile = dog.kind === 'seori'
        ? { w: 29, h: 7, a: 0.38 }
        : { w: 27, h: 7, a: 0.36 };
      const contactY = dog.y
        + (DOG_ALPHA_BOTTOM - 0.5 - DOG_CELL / 2) * artScale;
      g.fillStyle(0x010309, profile.a);
      g.fillEllipse(dog.x, contactY, profile.w * artScale, profile.h * artScale);
    }

    const boss = world.boss;
    if (boss && !boss.dead && BOSS_SHADOW[boss.kind]) {
      const profile = BOSS_SHADOW[boss.kind];
      const contactY = boss.y
        + spriteBottomOffsetY(boss.kind, 1, 0.75, 0) - 0.5;
      g.fillStyle(0x010309, profile.a);
      g.fillEllipse(boss.x, contactY, profile.w, profile.h);
    }

    // Locked paths and body-ground motion share this under-actor layer. It runs
    // after contact shadows so the marks remain legible, before every sprite so
    // they never look like laser beams painted across the animal.
    this.drawBehaviorGroundFx(world, artScale);

    if (!this.storm) return;
    const t = this.scene._artClock ?? 0;

    // Ground strikes: small elliptical rings sell rain against a top-down field.
    for (const r of this.ripples) {
      const ph = (r.phase + t * r.rate) % 1;
      if (ph > 0.42) continue;
      const k = ph / 0.42;
      g.lineStyle(1.2, 0x8ac8ef, 0.32 * (1 - k));
      g.strokeEllipse(r.x, r.y, r.size * (0.35 + k), r.size * 0.34 * (0.35 + k));
      if (k < 0.18) {
        g.lineStyle(1.4, 0xc8efff, 0.38 * (1 - k / 0.18));
        g.lineBetween(r.x, r.y, r.x - 4, r.y - 8);
        g.lineBetween(r.x, r.y, r.x + 5, r.y - 6);
      }
    }
  }

  _behaviorInView(enemy, behavior) {
    const view = this.scene.cameras?.main?.worldView;
    if (!view || !Number.isFinite(view.x) || !Number.isFinite(view.y)
        || !Number.isFinite(view.width) || !Number.isFinite(view.height)) return true;
    const margin = BEHAVIOR_MARK_VIEW_MARGIN;
    const inside = (x, y) => x >= view.x - margin && x <= view.x + view.width + margin
      && y >= view.y - margin && y <= view.y + view.height + margin;
    // An attacker may begin just outside the camera while its immutable landing
    // point is already on screen. Preserve that real crossing path without an
    // invented edge arrow.
    return inside(enemy.x, enemy.y)
      || (behavior.hasTarget && inside(behavior.targetX, behavior.targetY));
  }

  _drawMajorBehaviorTrack(enemy, behavior, artScale) {
    if (!behavior.hasTarget) return false;
    const p = clamp01(behavior.progress);
    // A major tell keeps only the immutable path owned by simulation. There is
    // no endpoint icon, decal, or renderer-guessed fallback; body motion carries
    // every ordinary commit/recovery state, while scene.js owns exact boss
    // attackGeometry.
    drawBrokenTrack(this.ground, enemy.x, enemy.y,
      behavior.targetX, behavior.targetY, 0.08 + p * 0.92,
      0xb9aa92, 0.10 + p * 0.16, Math.max(0.65, 0.9 * artScale), 4);
    return true;
  }

  drawBehaviorGroundFx(world, artScale) {
    const counts = this._behaviorFxCounts;
    counts.major = 0; counts.motion = 0; counts.recover = 0;
    counts.boss = 0; counts.total = 0;

    // Major warnings already have a world-owned token, so at most one is
    // expected. Still pick the closest visible locked path defensively if a
    // legacy/test world presents more than one. Ordinary motion and recovery
    // deliberately draw nothing here.
    const px = Number(world.player?.x) || 0;
    const py = Number(world.player?.y) || 0;
    let selected = null;
    let bestD2 = Infinity;
    for (const enemy of world.enemies ?? []) {
      const behavior = resolveEnemyBehavior(enemy, this._behaviorScratch);
      if (behavior.state !== 'tell' || !behavior.major || !behavior.hasTarget
          || !this._behaviorInView(enemy, behavior)) continue;
      const enemyD2 = (enemy.x - px) ** 2 + (enemy.y - py) ** 2;
      const targetD2 = (behavior.targetX - px) ** 2 + (behavior.targetY - py) ** 2;
      const d2 = Math.min(enemyD2, targetD2);
      if (d2 < bestD2) { selected = enemy; bestD2 = d2; }
    }
    if (!selected) return;
    const behavior = resolveEnemyBehavior(selected, this._behaviorScratch);
    if (this._drawMajorBehaviorTrack(selected, behavior, artScale)) {
      counts.major = 1;
      counts.total = 1;
    }
  }

  drawActorLight(world, player, artScale, tierColor) {
    const g = this.glow;
    const danger = clamp01((72 - world.stability) / 45);
    const breathe = 0.92 + 0.08 * Math.sin((this.scene._artClock ?? 0) * 3.4);

    // 178→120 and max alpha 0.60→0.40 both remove about one third. The soft
    // ellipse remains only as local separation from the field; separate solid
    // centre/orbit fills no longer bleach actors standing beside David.
    const R = 120 * artScale * breathe;
    this.keyLight.setPosition(player.x, player.y + 8)
      .setDisplaySize(R * 2.70, R * 1.42)
      .setAlpha((0.34 + danger * 0.06) * breathe)
      .setTint(this.look.key)
      .setVisible(true);

    this.drawShotTrails(world, artScale, tierColor);

    // The ribbon owns the path. These compact head lights only identify the
    // physical stone at its leading edge.
    for (const s of world.shots ?? []) {
      const shard = !!s.shard;
      const generation = Math.max(1, s.shardGen ?? 1);
      const monanShard = shard && s.stoneId === 'monan';
      const monanDiamond = !!s.diamond && s.stoneId === 'monan';
      const hierarchy = monanShard
        ? MONAN_SHARD_VISUAL.headScale
          * Math.max(0.58, 1 - (generation - 1) * MONAN_SHARD_VISUAL.generationFade)
          * (monanDiamond ? 1.05 : 1)
        : monanDiamond
          ? 1.18
        : 1;
      const speed = Math.hypot(s.vx, s.vy) || 1;
      const ux = s.vx / speed, uy = s.vy / speed;
      g.fillStyle(tierColor, (shard ? 0.08 : 0.12) * hierarchy);
      g.fillCircle(s.x, s.y, (shard ? 8 : 13) * artScale * hierarchy);
      g.fillStyle(0xfff7e8, (shard ? 0.15 : 0.27) * hierarchy);
      g.fillCircle(s.x, s.y, (shard ? 3.5 : 5.5) * artScale * hierarchy);
      // Wet-looking ground echo keeps fast shots connected to the field.
      g.fillStyle(tierColor, shard ? 0.035 : 0.07);
      g.fillEllipse(s.x - ux * 5, s.y + 12, (shard ? 18 : 34) * artScale * hierarchy,
                    (shard ? 4 : 7) * artScale * hierarchy);
      if (monanShard) {
        // 둥근 광구를 더 키우지 않고 실제 진행 방향을 가진 각진 조각만 한 겹 보탠다.
        const mark = Math.max(1.5, 3.2 * artScale * hierarchy);
        g.fillStyle(tierColor, 0.34 * hierarchy);
        g.fillRect(s.x - mark * 0.5, s.y - mark * 0.5, mark, mark);
        g.lineStyle(Math.max(1, 1.1 * artScale), 0xfff7e8, 0.42 * hierarchy);
        g.lineBetween(s.x - ux * 8 * artScale, s.y - uy * 8 * artScale, s.x, s.y);
      }
    }
  }

  drawShotTrails(world, artScale, tierColor) {
    const g = this.glow;
    const now = this.scene._artClock ?? world.t;
    const frame = ++this.trailFrame;

    for (const shot of world.shots ?? []) {
      let state = this.shotTrailMap.get(shot);
      if (!state) {
        const shard = !!shot.shard;
        state = {
          owner: shot,
          shard,
          shardGeneration: Math.max(1, shot.shardGen ?? 1),
          monanShard: shard && shot.stoneId === 'monan',
          monanDiamond: !!shot.diamond && shot.stoneId === 'monan',
          trail: new TrajectoryTrail(shard ? 8 : 14, shard ? 18 : 14, shard ? 3 : 5),
          turn: 0,
          seen: frame,
          deadAt: null,
          maxAge: shard ? 0.11 : 0.18,
          maxLength: (shard ? 72 : 82) * artScale,
        };
        this.shotTrailMap.set(shot, state);
        this.shotTrailStates.push(state);
      }

      state.seen = frame;
      state.deadAt = null;
      const turn = shot._trailTurn ?? 0;
      if (turn !== state.turn) {
        state.trail.sample(
          shot._trailTurnX ?? shot.x,
          shot._trailTurnY ?? shot.y,
          now,
          true,
        );
        state.turn = turn;
      }
      state.trail.sample(shot.x, shot.y, now);

      // Penetration is a visible promise: a stone built to continue through
      // bodies is allowed to carry a slightly longer history.
      const penetration = Math.max(0, Math.min(6, (shot.pierce ?? 0) + (shot.hits ?? 0)));
      const shardHierarchy = state.monanShard
        ? Math.max(0.58,
          1 - (state.shardGeneration - 1) * MONAN_SHARD_VISUAL.generationFade)
        : 1;
      const monanAge = state.monanShard ? MONAN_SHARD_VISUAL.trailAgeMul : 1;
      const monanLength = state.monanShard ? MONAN_SHARD_VISUAL.trailLengthMul : 1;
      state.maxAge = state.shard ? 0.11 * shardHierarchy * monanAge : 0.17 + penetration * 0.012;
      state.maxLength = (state.shard ? 72 * shardHierarchy * monanLength
        : 78 * (1 + penetration * 0.11)) * artScale;
    }

    this.mainTrails.length = 0;
    this.shardTrails.length = 0;
    let kept = 0;
    for (let i = 0; i < this.shotTrailStates.length; i++) {
      const state = this.shotTrailStates[i];
      if (state.seen !== frame) {
        if (state.deadAt == null) {
          // The owner object still contains its final simulation coordinate
          // even after World splices it from the live shot array.
          state.trail.sample(state.owner.x, state.owner.y, now);
          state.deadAt = now;
        }
        if (now - state.deadAt > 0.13) continue;
      }
      state.trail.trim(now, state.maxAge, state.maxLength);
      this.shotTrailStates[kept++] = state;
      if (state.trail.count > 1) {
        (state.shard ? this.shardTrails : this.mainTrails).push(state.trail);
      }
    }
    this.shotTrailStates.length = kept;

    for (const pass of this.mainTrailPasses) pass.color = tierColor;
    this.mainTrailPasses[2].color = 0xfff5dd;
    for (const pass of this.shardTrailPasses) pass.color = tierColor;

    const mainDensity = Math.min(1, 0.68 + 0.32 * Math.sqrt(18 / Math.max(18, this.mainTrails.length)));
    const shardDensity = Math.min(1, 0.58 + 0.42 * Math.sqrt(36 / Math.max(36, this.shardTrails.length)));
    drawTrailBatch(g, this.mainTrails, this.mainTrailPasses, {
      alphaScale: mainDensity,
      widthScale: artScale,
      smooth: true,
      maxTrails: 96,
    });
    drawTrailBatch(g, this.shardTrails, this.shardTrailPasses, {
      alphaScale: shardDensity,
      widthScale: artScale,
      smooth: false,
      maxTrails: 112,
    });
  }

  drawContactFx(world, artScale, tierColor) {
    const g = this.impact;
    const diamondSet = isDiamondSetPresentation(world.mods);
    const diamondAlpha = diamondSet ? diamondLocalAlpha(world) : 0;
    let diamondHitBudget = 0;
    let diamondKillBudget = 0;
    const nearest = this._contactFxNearest;
    resetNearestSlots(nearest.hits);
    resetNearestSlots(nearest.kills);
    const px = Number(world.player?.x) || 0;
    const py = Number(world.player?.y) || 0;

    // Select by event position, not enemy array order. These slots constrain
    // only the ordinary white swarm flash / normal kill burst; elite and ◆
    // rendering below bypass them unchanged.
    for (const e of world.enemies) {
      if (e.kind !== 'swarm' || pulse(world.t - (e.hitT ?? -9), 0.13) <= 0) continue;
      considerNearest(nearest.hits, e, (e.x - px) ** 2 + (e.y - py) ** 2);
    }
    for (const k of world.pops ?? []) {
      const age = world.t - k.t;
      if (k.big || age < 0 || age >= 0.38) continue;
      considerNearest(nearest.kills, k, (k.x - px) ** 2 + (k.y - py) ** 2);
    }

    // Non-lethal contact flashes. The sprite fill flash is backed by a brief
    // asymmetric spark so a hit still reads inside a crowd.
    for (const e of world.enemies) {
      const age = world.t - (e.hitT ?? -9);
      const a = pulse(age, 0.13);
      const diamond = diamondSet ? diamondBeat(age, 0.15, 0.24) : null;
      if (a <= 0 && !(diamond?.total > 0)) continue;
      const r = (e.kind === 'swarm' ? 11 : 19) * artScale;
      const seed = e.x * 0.071 + e.y * 0.113;
      const direction = Math.atan2(e.hitDy ?? Math.sin(seed), e.hitDx ?? Math.cos(seed));
      const ordinaryHit = e.kind !== 'swarm' || selectedNearest(nearest.hits, e);
      if (a > 0 && ordinaryHit) {
        g.fillStyle(0xffffff, 0.28 * a);
        g.fillCircle(e.x, e.y, r * 0.46);
        g.lineStyle(Math.max(1.3, 2.8 * a * artScale), 0xffffff, 0.88 * a);
        const rays = e.kind === 'swarm' ? 2 : 3;
        for (let i = 0; i < rays; i++) {
          const fan = i - (rays - 1) * 0.5;
          const ang = direction + fan * 0.43 + (hash(i + seed) - 0.5) * 0.13;
          const inner = r * 0.35;
          const outer = r * (1.05 + hash(i + seed) * 0.72);
          g.lineBetween(e.x + Math.cos(ang) * inner, e.y + Math.sin(ang) * inner,
                        e.x + Math.cos(ang) * outer, e.y + Math.sin(ang) * outer);
        }
      }
      // ◆◆◆◆는 명중점에서만 두 박자로 부서진다. 화면 밀도가 높아져도 스물넷을
      // 넘기지 않아 경고선과 배우 실루엣을 덮지 않는다.
      if (diamond?.total > 0 && diamondHitBudget++ < 24) {
        const q = diamond.total * diamondAlpha;
        const u = Math.min(1, Math.max(0, age / 0.38));
        g.lineStyle((1.2 + 3.4 * q) * artScale, 0xbff4ff, 0.72 * q);
        g.strokeCircle(e.x, e.y, r * (0.72 + u * 1.42));
        const pixels = e.kind === 'swarm' ? 4 : 7;
        g.fillStyle(0xf4ffff, 0.72 * q);
        for (let i = 0; i < pixels; i++) {
          const ang = direction + i / pixels * TAU + seed;
          const d = r * (0.52 + u * (1.15 + hash(seed + i) * 0.75));
          const s = Math.max(1.4, (2.2 + (i % 3)) * artScale * q);
          g.fillRect(e.x + Math.cos(ang) * d - s * 0.5,
            e.y + Math.sin(ang) * d - s * 0.5, s, s);
        }
      }
    }

    // Kill events are layered: hot core, irregular star, body-scale ring and
    // ballistic fragments. Elite kills get a second slower ring.
    for (const k of world.pops ?? []) {
      const age = world.t - k.t;
      const life = k.big ? 0.54 : 0.38;
      if (age < 0 || age >= life) continue;
      const u = age / life;
      const a = (1 - u) * (1 - u);
      const combo = k.big
        ? Math.min(2.1, 1 + Math.max(0, (k.n ?? 1) - 1) * 0.09)
        : Math.min(1.28, 1 + Math.max(0, (k.n ?? 1) - 1) * 0.025);
      const base = (k.big ? 34 : 18) * artScale * combo;
      const seed = k.x * 0.017 + k.y * 0.031;
      const direction = Math.atan2(k.dy ?? Math.sin(seed), k.dx ?? Math.cos(seed));
      const ordinaryKill = k.big || selectedNearest(nearest.kills, k);

      if (ordinaryKill) {
        g.fillStyle(tierColor, (k.big ? 0.25 : 0.17) * a);
        g.fillCircle(k.x, k.y, base * (0.45 + 0.75 * u));
        g.fillStyle(0xffffff, (k.big ? 0.72 : 0.40) * a);
        g.fillCircle(k.x, k.y, base * (k.big ? 0.18 : 0.13));

        const rays = k.big ? 11 : 3;
        g.lineStyle((k.big ? 4.2 : 2.0) * artScale * a, 0xffffff,
                    (k.big ? 0.94 : 0.76) * a);
        for (let i = 0; i < rays; i++) {
          const ang = k.big
            ? seed + i / rays * TAU
            : direction + (i - 1) * 0.46 + (hash(i + seed * 7) - 0.5) * 0.14;
          const jitter = 0.68 + hash(i + seed * 11) * 0.85;
          const inner = base * 0.18;
          const outer = base * jitter * (0.75 + 0.85 * u);
          g.lineBetween(k.x + Math.cos(ang) * inner, k.y + Math.sin(ang) * inner,
                        k.x + Math.cos(ang) * outer, k.y + Math.sin(ang) * outer);
        }

        g.lineStyle((k.big ? 5.5 : 1.8) * artScale * a, tierColor,
                    (k.big ? 0.75 : 0.34) * a);
        g.strokeEllipse(
          k.x, k.y,
          base * (k.big ? 2.8 : 1.7) * u + 8,
          base * (k.big ? 1.55 : 0.95) * u + 5,
        );

        // Ordinary crowd kills already read through the compact white burst.
        // Red fragments are reserved for big kills so the centre cannot turn
        // back into a thicket of red lines during swarm clear.
        g.lineStyle(2.0 * artScale * a, 0xff5b36, 0.72 * a);
        const fragments = k.big ? 10 : 0;
        for (let i = 0; i < fragments; i++) {
          const ang = k.big
            ? seed * 3 + i / fragments * TAU
            : direction + (i - 1) * 0.58;
          const d0 = base * (0.25 + u * 0.65);
          const d1 = d0 + base * (0.45 + hash(i + 77) * 0.65);
          g.lineBetween(k.x + Math.cos(ang) * d0, k.y + Math.sin(ang) * d0,
                        k.x + Math.cos(ang) * d1, k.y + Math.sin(ang) * d1);
        }
      }

      if (diamondSet && diamondKillBudget++ < 14) {
        const diamond = diamondBeat(age, 0.22, k.big ? 0.44 : 0.32);
        const q = diamond.total * diamondAlpha;
        if (q > 0) {
          const rr = base * (0.78 + u * 2.35);
          g.lineStyle((1.5 + 4.5 * q) * artScale, 0xbff4ff, 0.74 * q);
          g.strokeCircle(k.x, k.y, rr);
          g.lineStyle((1 + 2.2 * diamond.echo) * artScale, 0xf4ffff,
            0.52 * diamond.echo * diamondAlpha);
          g.strokeCircle(k.x, k.y, rr * 0.62);
          g.fillStyle(0xf4ffff, 0.82 * q);
          const pixels = k.big ? 14 : 8;
          for (let i = 0; i < pixels; i++) {
            const ang = seed * 1.7 + i / pixels * TAU;
            const d = base * (0.42 + u * (1.4 + hash(seed + i * 3) * 1.3));
            const s = Math.max(1.4, (2 + (i % 4)) * artScale * q);
            g.fillRect(k.x + Math.cos(ang) * d - s * 0.5,
              k.y + Math.sin(ang) * d - s * 0.5, s, s);
          }
        }
      }

      if (k.big) {
        g.lineStyle(2.2 * artScale * a, 0xffb15a, 0.58 * a);
        g.strokeCircle(k.x, k.y, base * (0.7 + u * 2.5));
      }
    }

    // Landing and overkill events get a filled core as well as their gameplay
    // ring from scene.js, producing a single heavy contact instead of a wireframe.
    const landAge = world.t - (world._landAt ?? -9);
    const landA = pulse(landAge, 0.30);
    if (landA > 0 && world._landXY) {
      const r = Math.max(22, world._landXY.r ?? 22);
      g.fillStyle(tierColor, 0.20 * landA);
      g.fillCircle(world._landXY.x, world._landXY.y, r * (1.25 - landA * 0.35));
      g.fillStyle(0xffffff, 0.24 * landA);
      g.fillCircle(world._landXY.x, world._landXY.y, r * 0.23);
    }
  }

  drawDiamondWeaponSignature(world, player, artScale, tierColor) {
    const signature = diamondSignature(world.mods?.stoneId, world.mods?.tier);
    if (!signature) return;
    const g = this.impact;
    const volleyAge = world.t - (world._volleyAt ?? -9);
    const clarity = diamondLocalAlpha(world);

    if (signature === 'fan-pulse' && volleyAge >= 0 && volleyAge < 0.55) {
      // 잔돌 ◆: 본 부채가 열리고 0.13초 뒤 더 넓은 굴절 부채가 한 번 더 답한다.
      const beat = diamondBeat(volleyAge, 0.24, 0.34);
      const a = beat.total * clarity;
      const u = Math.min(1, volleyAge / 0.44);
      const origin = world._volleyXY ?? player;
      const aim = Number(world._volleyAngle) || 0;
      const count = Math.max(3, world._volleyN | 0);
      const span = Math.min(1.28, 0.18 * Math.max(2, count - 1));
      const inner = (8 + 28 * u) * artScale;
      const outer = (48 + 164 * u) * artScale;
      g.lineStyle((2.0 + 6.2 * a) * artScale, 0xf8ffff, 0.90 * a);
      for (let i = 0; i < count; i++) {
        const q = count === 1 ? 0.5 : i / (count - 1);
        const ang = aim - span * 0.5 + span * q;
        g.lineBetween(
          origin.x + Math.cos(ang) * inner, origin.y + Math.sin(ang) * inner,
          origin.x + Math.cos(ang) * outer, origin.y + Math.sin(ang) * outer,
        );
        const s = Math.max(1.5, (2.2 + 2.6 * a) * artScale);
        g.fillStyle(0xf4ffff, 0.82 * a);
        g.fillRect(origin.x + Math.cos(ang) * outer - s * 0.5,
          origin.y + Math.sin(ang) * outer - s * 0.5, s, s);
      }
      g.lineStyle((1.5 + 3.4 * a) * artScale, tierColor, 0.76 * a);
      g.beginPath();
      g.arc(origin.x, origin.y, outer, aim - span * 0.5, aim + span * 0.5, false);
      g.strokePath();
      if (beat.echo > 0) {
        g.lineStyle((1.1 + 2.8 * beat.echo) * artScale, 0xf4ffff,
          0.52 * beat.echo * clarity);
        g.beginPath();
        g.arc(origin.x, origin.y, outer * 0.76,
          aim - span * 0.58, aim + span * 0.58, false);
        g.strokePath();
      }
    }

    if (signature === 'physical-line-lead') {
      // 고른 돌 ◆: 광선 머리가 아니라 실제 돌 하나가 밀고 나가며 뒤의 조밀한 돌선을 이끈다.
      let lead = null;
      let leadD2 = -1;
      for (const shot of world.shots ?? []) {
        if (shot.shard || !shot.diamond || shot.stoneId !== 'goreun') continue;
        const d2 = (shot.x - player.x) ** 2 + (shot.y - player.y) ** 2;
        if (d2 > leadD2) { lead = shot; leadD2 = d2; }
      }
      if (lead) {
        const speed = Math.hypot(lead.vx, lead.vy) || 1;
        const ux = lead.vx / speed, uy = lead.vy / speed;
        const breathe = (0.82 + 0.18 * Math.sin((this.scene._artClock ?? world.t) * 19) ** 2)
          * clarity;
        g.fillStyle(0x403b36, 0.94 * breathe);
        g.fillCircle(lead.x, lead.y, 10.2 * artScale);
        g.fillStyle(0xb8ad99, 0.96 * breathe);
        g.fillCircle(lead.x - ux * 2.1, lead.y - uy * 2.1, 6.8 * artScale);
        g.fillStyle(0xf4ffff, 0.96 * breathe);
        g.fillCircle(lead.x + ux * 4.8, lead.y + uy * 4.8, 2.5 * artScale);
        // 빛구슬 대신 실제 선두 돌을 감싸는 각진 ◆ 테두리로 한 단계 위 물성을 만든다.
        const pr = 13.5 * artScale;
        g.lineStyle(2.2 * artScale, 0xbff4ff, 0.78 * breathe);
        g.lineBetween(lead.x, lead.y - pr, lead.x + pr, lead.y);
        g.lineBetween(lead.x + pr, lead.y, lead.x, lead.y + pr);
        g.lineBetween(lead.x, lead.y + pr, lead.x - pr, lead.y);
        g.lineBetween(lead.x - pr, lead.y, lead.x, lead.y - pr);
        for (let i = 1; i <= 5; i++) {
          const side = i % 2 ? 1 : -1;
          const x = lead.x - ux * (8 + i * 6) - uy * side * i * 1.5;
          const y = lead.y - uy * (8 + i * 6) + ux * side * i * 1.5;
          g.fillStyle(i % 2 ? 0xbff4ff : 0x8b7b66, (0.44 / i) * clarity);
          g.fillCircle(x, y, Math.max(1.2, (4.3 - i * 0.62) * artScale));
        }
        const beat = diamondBeat(volleyAge, 0.22, 0.36);
        if (beat.total > 0) {
          const r = (18 + (1 - beat.total) * 28) * artScale;
          g.lineStyle((1.4 + 4 * beat.total) * artScale, 0xf4ffff,
            0.68 * beat.total * clarity);
          g.strokeCircle(lead.x, lead.y, r);
        }
      }
    }

    if (signature === 'shard-generations') {
      // 모난 돌 ◆: 원탄 > 1차 조각 > 연쇄 조각 순서가 크기·밝기·잔상 길이로 계속 읽힌다.
      if (volleyAge >= 0 && volleyAge < 0.48) {
        const beat = diamondBeat(volleyAge, 0.22, 0.34);
        const a = beat.total * clarity;
        const origin = world._volleyXY ?? player;
        g.fillStyle(0xffffff, 0.56 * a);
        g.fillCircle(origin.x, origin.y, (13 + 22 * (1 - a)) * artScale);
        g.lineStyle((1.4 + 4.6 * a) * artScale, tierColor, 0.72 * a);
        g.strokeCircle(origin.x, origin.y, (20 + 42 * (1 - a)) * artScale);
      }
      const shardAge = world.t - (world._shardAt ?? -9);
      if (shardAge >= 0 && shardAge < MONAN_SHARD_VISUAL.eventLife && world._shardXY) {
        const generation = Math.max(1, world._shardGeneration | 0);
        const hierarchy = Math.max(0.58,
          1 - (generation - 1) * MONAN_SHARD_VISUAL.generationFade);
        const beat = diamondBeat(shardAge, 0.24, 0.40);
        const a = beat.total * hierarchy * clarity;
        const vectors = liveMonanShardVectors(world);
        g.fillStyle(0xffffff, 0.62 * a);
        g.fillCircle(world._shardXY.x, world._shardXY.y, 8.5 * artScale * hierarchy);
        for (const vector of vectors) {
          // 원점에서 실제 탄체 좌표까지만 잇는다. 앞날을 예측해 선을 더 뻗지 않으므로
          // 보이지 않는 광역·관통 판정처럼 오해할 가짜 반경이 생기지 않는다.
          const startX = world._shardXY.x + vector.dx * 0.18;
          const startY = world._shardXY.y + vector.dy * 0.18;
          g.lineStyle((1.8 + 4.8 * a) * artScale, tierColor, 0.86 * a);
          g.lineBetween(startX, startY, vector.shot.x, vector.shot.y);
          const size = Math.max(1.4, (2.2 + generation * 0.55) * artScale * a);
          g.fillStyle(0xf4ffff, 0.78 * a);
          g.fillRect(vector.shot.x - size * 0.5, vector.shot.y - size * 0.5, size, size);
          if (beat.echo > 0 && vector.distance > 2) {
            const tail = Math.min(vector.distance * 0.46, 12 * artScale);
            g.lineStyle((1 + 2.4 * beat.echo) * artScale, 0xf4ffff,
              0.48 * beat.echo * clarity * hierarchy);
            g.lineBetween(vector.shot.x - vector.ux * tail, vector.shot.y - vector.uy * tail,
              vector.shot.x, vector.shot.y);
          }
        }
      }
    }

    if (signature === 'impact-ring-afterglow') {
      // 무거운 돌 ◆: 접촉 핵 → 큰 충격 고리 → 한 박자 늦은 재폭발 잔광.
      const age = world.t - (world._landAt ?? -9);
      if (age >= 0 && age < 0.92 && world._landXY) {
        const feel = world._landFeel === false ? 0.72 : 1;
        const stage = Math.max(0, world._landStage | 0);
        const x = world._landXY.x, y = world._landXY.y;
        const radius = Math.max(24, world._landXY.r ?? 24);
        const beat = diamondBeat(age, stage > 0 ? 0.26 : 0.22, stage > 0 ? 0.58 : 0.48);
        const core = beat.main * feel * clarity;
        const ringU = Math.min(1, age / (stage > 0 ? 0.56 : 0.46));
        const ringA = beat.total * feel * clarity;
        const after = beat.echo * feel * clarity;
        if (core > 0) {
          g.fillStyle(0xffffff, 0.74 * core);
          g.fillCircle(x, y, radius * (0.13 + 0.22 * core));
        }
        if (ringA > 0) {
          g.lineStyle((2.4 + 8.2 * ringA) * artScale, tierColor, 0.92 * ringA);
          g.strokeCircle(x, y, radius * (0.38 + ringU * 1.82));
        }
        if (after > 0) {
          g.fillStyle(tierColor, 0.16 * after);
          g.fillCircle(x, y, radius * (0.42 + 0.46 * (1 - after)));
          g.lineStyle((1.6 + 5.2 * after) * artScale, 0xf5ffff, 0.66 * after);
          g.strokeCircle(x, y, radius * (0.78 + (1 - after) * 1.52));
          g.fillStyle(0xf4ffff, 0.82 * after);
          for (let i = 0; i < 16; i++) {
            const ang = i / 16 * TAU + stage * 0.17;
            const d = radius * (0.48 + (1 - after) * (1.12 + (i % 4) * 0.16));
            const s = Math.max(1.5, (2.3 + i % 3) * artScale * after);
            g.fillRect(x + Math.cos(ang) * d - s * 0.5,
              y + Math.sin(ang) * d - s * 0.5, s, s);
          }
          if (stage > 0) {
            g.lineStyle((1.2 + 3.4 * after) * artScale, tierColor, 0.38 * after);
            g.strokeCircle(x, y, radius * (0.34 + stage * 0.14 + (1 - after) * 0.88));
          }
        }
      }
    }
    // napjak은 모든 자국과 8·16·24·32 박자, chadol은 궤도 접촉 수축을 scene.js가 그린다.
  }

  drawDiamondSetFireworks(world, player, artScale, tierColor) {
    if (!isDiamondSetPresentation(world.mods)) return;
    const clarity = diamondLocalAlpha(world);
    const g = this.impact;
    const now = this.scene._artClock ?? world.t;

    const pixelBurst = (x, y, age, radius, count, seed = 0) => {
      const beat = diamondBeat(age, 0.22, 0.38);
      const q = beat.total * clarity;
      if (q <= 0) return;
      const u = Math.min(1, Math.max(0, age / 0.52));
      g.lineStyle((1.4 + 4.8 * q) * artScale, 0xbff4ff, 0.78 * q);
      g.strokeCircle(x, y, radius * (0.42 + u * 1.42));
      if (beat.echo > 0) {
        g.lineStyle((1.1 + 2.4 * beat.echo) * artScale, 0xf4ffff,
          0.54 * beat.echo * clarity);
        g.strokeCircle(x, y, radius * (0.20 + u * 0.92));
      }
      g.fillStyle(0xf4ffff, 0.86 * q);
      for (let i = 0; i < count; i++) {
        const ang = seed + i / count * TAU + (hash(seed * 17 + i) - 0.5) * 0.18;
        const d = radius * (0.30 + u * (0.72 + hash(seed + i * 5) * 1.16));
        const s = Math.max(1.5, (2.2 + i % 4) * artScale * q);
        g.fillRect(x + Math.cos(ang) * d - s * 0.5,
          y + Math.sin(ang) * d - s * 0.5, s, s);
      }
    };

    // 네 부위가 결속되면 발사점에서 작은 픽셀 폭죽이 먼저 열리고, 0.13초 뒤
    // 같은 자리의 청백색 고리가 한 번 더 답한다. 화면 좌표 전체를 채우지 않는다.
    const volleyAge = world.t - (world._volleyAt ?? -9);
    if (volleyAge >= 0 && volleyAge < 0.56) {
      const origin = world._volleyXY ?? player;
      const seed = Number(world._volleySeq) || 0;
      pixelBurst(origin.x, origin.y, volleyAge, 50 * artScale, 12, seed);
      const aim = Number(world._volleyAngle) || 0;
      const beat = diamondBeat(volleyAge, 0.20, 0.34);
      const q = beat.total * clarity;
      g.lineStyle((1.2 + 3.8 * q) * artScale, tierColor, 0.68 * q);
      for (const off of [-0.22, 0, 0.22]) {
        const ang = aim + off;
        const d0 = (18 + 18 * (1 - q)) * artScale;
        const d1 = (72 + 90 * (1 - q)) * artScale;
        g.lineBetween(origin.x + Math.cos(ang) * d0, origin.y + Math.sin(ang) * d0,
          origin.x + Math.cos(ang) * d1, origin.y + Math.sin(ang) * d1);
      }
    }

    // 살아 있는 탄체에는 크고 흐린 광구 대신 작은 각진 광점과 두 칸짜리 꼬리만 둔다.
    // 최대 열여덟 발만 받아 대량 파편 빌드에서도 Graphics 예산을 고정한다.
    let projectiles = 0;
    for (const shot of world.shots ?? []) {
      if (shot.shard || !shot.diamond || projectiles++ >= 18) continue;
      const speed = Math.hypot(shot.vx, shot.vy) || 1;
      const ux = shot.vx / speed, uy = shot.vy / speed;
      const flicker = 0.68 + 0.32 * Math.sin(now * 18 + (shot.volleyIndex ?? 0) * 1.7) ** 2;
      const q = clarity * flicker;
      const s = (2.3 + flicker * 1.8) * artScale;
      g.fillStyle(0xf4ffff, 0.88 * q);
      g.fillRect(shot.x - s * 0.5, shot.y - s * 0.5, s, s);
      g.fillStyle(tierColor, 0.58 * q);
      for (let i = 1; i <= 2; i++) {
        const p = s * (0.76 - i * 0.18);
        g.fillRect(shot.x - ux * i * 7 * artScale - p * 0.5,
          shot.y - uy * i * 7 * artScale - p * 0.5, p, p);
      }
    }

    const shardAge = world.t - (world._shardAt ?? -9);
    // 모난 돌은 위의 실제 탄체 벡터 연출이 이 자리를 맡는다. 균등 난수 폭죽을 다시
    // 얹으면 실제로는 없는 방향에도 피해가 난 듯 보이므로 다른 돌의 결속 연출에만 둔다.
    if (world.mods?.stoneId !== 'monan'
        && world._shardXY && shardAge >= 0 && shardAge < 0.56) {
      pixelBurst(world._shardXY.x, world._shardXY.y, shardAge,
        64 * artScale, 14, Number(world._shardSeq) || 0);
    }
    const landAge = world.t - (world._landAt ?? -9);
    if (world._landXY && landAge >= 0 && landAge < 0.62) {
      pixelBurst(world._landXY.x, world._landXY.y, landAge,
        Math.max(42, (world._landXY.r ?? 24) * 0.92), 16, Number(world._landSeq) || 0);
    }
    const orbitAge = world.t - (world._orbitAt ?? -9);
    if (orbitAge >= 0 && orbitAge < 0.46) {
      pixelBurst(player.x, player.y - 4, orbitAge,
        Math.max(42, (world._orbitR ?? 34) * 0.44), 10, Number(world._orbitN) || 0);
    }
  }

  drawWeather(world) {
    const g = this.weather;
    const t = this.scene._artClock ?? 0;
    const mode = this.look.atmosphere;

    if (mode === 'rain') {
      const wind = 150 + 34 * Math.sin(t * 0.21);
      for (const r of this.rain) {
        const span = H + 260;
        const y = wrap(r.y + t * r.speed, span) - 130;
        const x = wrap(r.x + t * wind + y * r.drift, W + 360) - 180;
        const near = r.width > 2;
        g.lineStyle(r.width, near ? 0xb9e6ff : 0x78afd2, r.alpha);
        g.lineBetween(x, y, x - r.len * r.drift, y - r.len);
      }
      for (let i = 0; i < 5; i++) {
        const y = 130 + i * 220 + Math.sin(t * (0.12 + i * 0.017) + i) * 44;
        const x = wrap(t * (22 + i * 7) + i * 390, W + 700) - 350;
        g.fillStyle(0x8dbbd0, 0.018 + i * 0.003);
        g.fillEllipse(x, y, 860 + i * 90, 78 + i * 11);
      }
      return;
    }

    if (mode === 'wind' || mode === 'canyon') {
      const strong = mode === 'wind';
      const color = strong ? 0xa7d2e8 : 0xd6aa78;
      const count = strong ? this.gusts.length : 18;
      for (let i = 0; i < count; i++) {
        const q = this.gusts[i];
        const x = wrap(q.x + t * q.speed * (strong ? 1.25 : 0.72), W + 600) - 300;
        const y = q.y + Math.sin(t * 0.8 + i) * (strong ? 24 : 9);
        const tilt = strong ? -0.20 : 0.035;
        g.lineStyle(strong && i % 7 === 0 ? 2.2 : 1.1, color,
                    q.alpha * (strong ? 1.0 : 0.58));
        g.lineBetween(x, y, x - q.len, y - q.len * tilt);
      }
      return;
    }

    if (mode === 'shadows') {
      for (let i = 0; i < 6; i++) {
        const x = wrap(t * (34 + i * 7) + i * 410, W + 920) - 460;
        const y = 110 + i * 190 + Math.sin(t * 0.18 + i) * 70;
        g.fillStyle(0x02030c, 0.020 + i * 0.002);
        g.fillEllipse(x, y, 1040, 116);
      }
      for (let i = 0; i < 36; i++) {
        const m = this.motes[i];
        const x = wrap(m.x + t * m.speed * 0.22, W);
        const y = wrap(m.y - t * 17 + Math.sin(t + i) * 18, H);
        g.lineStyle(1.6, 0x9b8fe8, 0.10 + m.bright * 0.12);
        g.lineBetween(x, y, x - 12 - m.size * 5, y + 5);
      }
      return;
    }

    if (mode === 'crows') {
      for (let i = 0; i < 4; i++) {
        const x = wrap(t * (18 + i * 5) + i * 530, W + 800) - 400;
        const y = 190 + i * 240 + Math.sin(t * 0.14 + i) * 52;
        g.fillStyle(0x2d7767, 0.018 + i * 0.003);
        g.fillEllipse(x, y, 980, 92);
      }
      for (let i = 0; i < 46; i++) {
        const m = this.motes[i];
        const x = wrap(m.x + t * m.speed * 0.34, W + 80) - 40;
        const y = wrap(m.y + t * (22 + m.speed * 0.12), H + 80) - 40;
        g.lineStyle(1.3 + m.size * 0.25, 0x07110f, 0.18 + m.bright * 0.16);
        g.lineBetween(x, y, x - 8 - m.size * 6, y + 10 + m.size * 3);
      }
      return;
    }

    if (mode === 'guides') {
      for (let i = 0; i < 48; i++) {
        const m = this.motes[i];
        const x = wrap(m.x + Math.sin(t * 0.37 + i) * 34 + t * m.drift * 3, W);
        const y = wrap(m.y - t * (8 + m.speed * 0.12), H);
        const twinkle = 0.35 + 0.65 * Math.abs(Math.sin(t * 1.7 + m.phase * TAU));
        g.fillStyle(i % 5 === 0 ? 0xffe8bd : 0xb8d8ff, (0.13 + m.bright * 0.18) * twinkle);
        g.fillCircle(x, y, m.size * (i % 5 === 0 ? 1.8 : 1.15));
        if (i % 13 === 0) {
          g.lineStyle(1.2, 0xd8ebff, 0.16 * twinkle);
          g.strokeCircle(x, y, 8 + twinkle * 10);
        }
      }
      return;
    }

    const isCinder = mode === 'cinders';
    const isAmbush = mode === 'ambush';
    const count = isCinder ? 68 : isAmbush ? 42 : mode === 'dust' ? 48 : 30;
    const color = isCinder ? 0xff7845 : isAmbush ? 0xe75a6f
                : mode === 'dust' ? 0xc99a6c : 0xffbd65;
    for (let i = 0; i < count; i++) {
      const m = this.motes[i];
      const rising = mode === 'embers' || isCinder;
      const x = wrap(m.x + t * m.drift * (rising ? 14 : 28)
                     + Math.sin(t * 0.7 + i) * (rising ? 12 : 24), W);
      const y = rising
        ? H - wrap(H - m.y + t * m.speed * (isCinder ? 0.60 : 0.28), H + 40)
        : wrap(m.y + t * m.speed * (isAmbush ? 0.18 : 0.10), H);
      const flicker = 0.45 + 0.55 * Math.abs(Math.sin(t * 2.1 + m.phase * TAU));
      g.fillStyle(color, (0.08 + m.bright * (isCinder ? 0.28 : 0.15)) * flicker);
      if (rising) {
        g.fillRect(x, y, Math.max(1, m.size * 0.65), 2 + m.size * (isCinder ? 3.4 : 2.2));
      } else {
        g.fillCircle(x, y, m.size * 0.65);
      }
    }

    if (isAmbush) {
      const sweep = Math.max(0, 1 - Math.abs(wrap(t, 8.4) - 4.2) / 0.42);
      if (sweep > 0) {
        g.fillStyle(0xa31434, 0.025 * sweep);
        g.fillRect(0, 0, 110 + 120 * sweep, H);
        g.fillRect(W - 110 - 120 * sweep, 0, 110 + 120 * sweep, H);
      }
    }
  }

  lightningAt(t) {
    const phase = wrap(t + 4.1, 14.7);
    const flash = (center, width, amp) => {
      const d = Math.abs(phase - center);
      return d < width ? amp * (1 - d / width) : 0;
    };
    return flash(1.82, 0.055, 1) + flash(2.06, 0.035, 0.72);
  }

  drawScreenResponse(world, tierColor = 0xffffff) {
    const grade = this.screenGrade;
    const glow = this.screenGlow;
    const clock = this.scene._artClock ?? world.t ?? 0;

    // 위험도 비네트는 지속 환경층이다. 순간 사건과 경쟁하지 않고, 플레이 중심은 열어 둔다.
    const risk = clamp01((66 - world.stability) / 38);
    const edgeColor = risk > 0.08 ? 0x4a0614 : this.look.edge;
    const edgeA = 0.12 + risk * 0.28;
    const D = 118 + risk * 104;
    for (let i = 0; i < 6; i++) {
      const k = 1 - i / 6;
      const d = D * (i + 1) / 6;
      grade.fillStyle(edgeColor, edgeA * k * 0.19);
      grade.fillRect(0, 0, W, d);
      grade.fillRect(0, H - d, W, d);
      grade.fillRect(0, 0, d, H);
      grade.fillRect(W - d, 0, d, H);
    }

    let normalFlash = 0;
    let eliteFlash = 0;
    let comboFlash = 0;
    for (const kill of world.pops ?? []) {
      const age = world.t - kill.t;
      normalFlash = Math.max(normalFlash, pulse(age, 0.14));
      if (kill.big) eliteFlash = Math.max(eliteFlash, pulse(age, 0.22));
      if ((kill.n ?? 0) >= 6) {
        comboFlash = Math.max(comboFlash,
          pulse(age, 0.16) * Math.min(1, (kill.n ?? 0) / 12));
      }
    }

    // 장착 섬광도 이 단일 채널에 들어온다. 무기 자체의 색·궤도 확대는 scene.js에 그대로 남는다.
    const equipAge = world.t - (world._equipAt ?? -9);
    const equipFlash = equipAge >= 0 && equipAge < EQUIP_FX.sec
      ? (1 - equipAge / EQUIP_FX.sec) ** 2 * Math.min(1, 0.42 + (world._equipTier ?? 1) * 0.10)
      : 0;
    const equipKind = (world._equipTier ?? 1) >= 6 ? 'diamond' : 'normal';

    const presentAge = world.t - (world._presentAt ?? -9);
    const presentLife = {
      hurt: 0.20, boss: 0.32, elite: 0.24, diamond: 0.23, combo: 0.18, normal: 0.16,
    }[world._presentKind] ?? 0;
    const presentFlash = presentLife > 0
      ? pulse(presentAge, presentLife) * clamp01(world._presentPower ?? 1)
      : 0;

    // major tell은 화면 반응 우선순위만 선점한다. 실제 모양은 세계 좌표 레이어가 소유한다.
    let warningProgress = -1;
    for (const enemy of world.enemies ?? []) {
      const progress = enemyWarningProgress(enemy, ACT.charge.tell);
      if (progress < 0) continue;
      if (progress > warningProgress) warningProgress = progress;
    }
    const boss = world.boss;
    if (boss && !boss.dead) {
      const progress = bossWarningProgress(boss);
      if (progress > warningProgress) warningProgress = progress;
    }
    const warningFlash = warningProgress >= 0 ? 0.52 + warningProgress * 0.48 : 0;
    // 경보는 우선순위 중재에는 남지만 화면 가장자리에서 안쪽으로 뻗던 14개의 방사선과
    // 카메라 좌표의 대체 원은 그리지 않는다. 특히 N39에서 실제 attackGeometry보다 훨씬
    // 큰 가짜 공격 범위처럼 읽혔다. 회피 정보는 몸의 잠금 방향과 실제 보스 이동·앞발·발톱
    // 기하만 전달한다.

    const lightning = this.storm ? Math.min(1, this.lightningAt(clock)) : 0;
    const candidates = [
      { kind: 'warning', strength: warningFlash, source: 'telegraph' },
      { kind: world._presentKind, strength: presentFlash, source: 'presentation' },
      { kind: 'elite', strength: eliteFlash, source: 'elite-kill' },
      { kind: equipKind, strength: equipFlash, source: 'equip' },
      { kind: 'combo', strength: comboFlash, source: 'combo' },
      { kind: 'normal', strength: normalFlash, source: 'normal-kill' },
      { kind: 'environment', strength: lightning, source: 'lightning' },
    ];
    // 사건 우선순위는 카메라·히트스톱·국소 문양의 서열을 정하는 데 계속 쓴다.
    // 다만 빨강·흰색·등급색·번개색으로 화면 전체를 순간 채우던 ADD fill은 없앴다.
    // 타격 파편, 충격파, 경고 고리, 장비 시그니처와 실제 번개 줄기는 각자의 위치에
    // 그대로 남으므로 전투 정보는 잃지 않고 눈을 때리는 전면 섬광만 사라진다.
    const response = chooseScreenResponse(candidates);

    // 콤보의 속도 문양은 사건 증거로 남되, 더 높은 경보가 채널을 잡으면 뒤로 물린다.
    if (comboFlash > 0) {
      const lineRank = response?.kind === 'combo' ? 1
        : response?.kind === 'warning' || response?.kind === 'hurt' ? 0.24 : 0.42;
      for (let i = 0; i < 12; i++) {
        const y = 70 + i * 86;
        const len = 90 + hash(i + world.t * 0.01) * 170;
        glow.lineStyle(1.5 + (i % 3), 0xfff1d2, 0.16 * comboFlash * lineRank);
        glow.lineBetween(0, y, len, y - 18);
        glow.lineBetween(W, H - y, W - len, H - y + 18);
      }
    }

    // 번개 본체는 공간 안의 사건이므로 항상 남고, 화면 전체 번쩍임만 위 중재를 따른다.
    if (this.storm) {
      const phase = wrap(clock + 4.1, 14.7);
      const bolt = Math.max(0, 1 - Math.abs(phase - 1.82) / 0.12);
      if (bolt > 0) {
        const cycle = Math.floor((clock + 4.1) / 14.7);
        const tx = 330 + hash(cycle + 4301) * (W - 660);
        const ty = 520 + hash(cycle + 4513) * 310;
        let x0 = tx - 180 + hash(cycle + 4703) * 360;
        let y0 = -30;
        for (let i = 1; i <= 8; i++) {
          const y1 = ty * i / 8;
          const x1 = i === 8 ? tx
            : tx + (1 - i / 8) * (hash(cycle * 19 + i + 4889) - 0.5) * 210;
          glow.lineStyle(13 * bolt, 0x8acfff, 0.055 * bolt);
          glow.lineBetween(x0, y0, x1, y1);
          glow.lineStyle(2.2 * bolt, 0xf2fbff, 0.92 * bolt);
          glow.lineBetween(x0, y0, x1, y1);
          x0 = x1; y0 = y1;
        }
        glow.fillStyle(0xb9e8ff, 0.14 * bolt);
        glow.fillEllipse(tx, ty + 8, 210 * bolt, 64 * bolt);
      }
    }

    // 사자의 침묵은 소리 버스뿐 아니라 느린 무채색 환경 호흡으로도 계속 증명한다.
    if (boss?.kind === 'lion' && !boss.dead && !world.finale) {
      const silence = 0.5 + 0.5 * Math.sin(clock * 0.72);
      grade.fillStyle(0x87909c, 0.012 + 0.010 * silence);
      grade.fillRect(0, 0, W, H);
    }

    // ⚠ **날짜로 판별하면 안 된다** 【40일 개편 2026-08-03】 — 구 N30(곰의 밤)은
    //   40일에서 **40일**로 갔고, 이름 N30 은 이제 「세 방향의 밤」이다.
    if (cardIdOf(this.nightKey) === 'N30') {
      const dread = (0.5 + 0.5 * Math.sin(clock * 1.55)) * risk;
      if (dread > 0.02) {
        grade.fillStyle(0x5a0803, 0.025 * dread);
        grade.fillRect(0, 0, W, H);
      }
    }
  }

}
